// Shared ordering for GENERIC (org) trees so the on-screen table, CSV export and
// print output all match. Departments/groups are ordered by their Group rank
// letter (attrs.gx3, A–Z; department rank = earliest letter among members;
// unranked groups always last). People within a group rank by Salary Grade
// (high → low) or Age, depending on rankBy.
//
// IDEA override (Indexing for Designation and Exigency Analysis): the Master /
// Submaster flags on the Designation and Exigency categories trump the normal
// order. Priority (top → bottom):
//   Designation-Master > Exigency-Master > Designation-Submaster > Exigency-Submaster
//   > Salary Grade (high → low) > Alphabetical (last, first).
// A unit that contains any Master/Submaster also floats above the normal A–Z
// group-rank order, so a Runner can re-pivot the whole diagram instantly.
const UNASSIGNED = "Unassigned";
export const gradeOfPerson = (p) => Number(p?.attrs?.salary_grade || 0);

export const priorityRank = (p) => {
  const d = (p?.attrs?.desig_rank || "").toLowerCase();
  const e = (p?.attrs?.exig_rank || "").toLowerCase();
  if (d === "master") return 0;
  if (e === "master") return 1;
  if (d === "submaster") return 2;
  if (e === "submaster") return 3;
  return 9;
};

export function orderGenericGroups(people = [], { dir = "asc", rankBy = "salary_grade", rawAge = () => null, applyIdea = true } = {}) {
  const pr = (p) => (applyIdea ? priorityRank(p) : 9);
  const map = new Map();
  people.forEach((p) => {
    const name = (p.attrs?.dept || "").trim() || UNASSIGNED;
    if (!map.has(name)) map.set(name, { name, rank: "", members: [] });
    const g = map.get(name);
    g.members.push(p);
    const r = (p.attrs?.gx3 || "").trim().toUpperCase();
    if (r && (!g.rank || r < g.rank)) g.rank = r;
  });
  const val = (p) => (rankBy === "age" ? (rawAge(p) ?? -1) : gradeOfPerson(p));
  const memberSort = (a, b) =>
    (pr(a) - pr(b)) ||
    (val(b) - val(a)) ||
    String(a.last || "").localeCompare(String(b.last || "")) ||
    String(a.first || "").localeCompare(String(b.first || ""));
  const arr = [...map.values()].map((g) => ({ ...g, members: [...g.members].sort(memberSort) }));
  const key = (g) => g.rank || "\uffff"; // unranked sorts last regardless of direction
  const topGrade = (g) => g.members.reduce((m, p) => Math.max(m, gradeOfPerson(p)), 0);
  const topPriority = (g) => g.members.reduce((m, p) => Math.min(m, pr(p)), 9);
  // same-rank tiebreak: strongest unit first (highest salary grade), then name
  const tie = (a, b) => (topGrade(b) - topGrade(a)) || a.name.localeCompare(b.name);
  arr.sort((a, b) => {
    // Units containing a Master/Submaster float above the normal rank-letter order.
    const pa = topPriority(a), pb = topPriority(b);
    if (pa !== pb) return pa - pb;
    const ak = key(a), bk = key(b);
    if (ak === "\uffff" && bk === "\uffff") return tie(a, b);
    if (ak === "\uffff") return 1;
    if (bk === "\uffff") return -1;
    if (ak === bk) return tie(a, b);
    return dir === "asc" ? ak.localeCompare(bk) : bk.localeCompare(ak);
  });
  return arr;
}

export function orderGenericFlat(people, opts) {
  return orderGenericGroups(people, opts).flatMap((g) => g.members);
}
